import React from "react";

import cc from "classcat";
import { useBreakpoints } from "@narmi/design_system";

import {
  useIsSidebarEnabled,
  useSidebarContainer,
} from "../../modules/sidebar/sidebar.hooks";

import styles from "./styles.module.scss";

export interface PageLayoutProps {
  /**
   * Layout type for the content area
   * - single: Full-width single column
   * - dual-column: Two-column grid on desktop
   * - triple-column: Three-column grid on desktop
   */
  layout?: "single" | "dual-column" | "triple-column";
  /**
   * Custom grid column ratio (e.g., "2fr 1fr" for dual-column)
   * Used when layout is "dual-column" or "triple-column"
   */
  columnRatio?: string;
  /**
   * Maximum width constraint based on NDS breakpoints
   * - s: 480px
   * - m: 768px
   * - l: 1280px
   * - xl: 1440px (default)
   * - full: No constraint
   */
  maxWidth?: "s" | "m" | "l" | "xl" | "full";
  /**
   * Content to display in the page header
   */
  header?: React.ReactNode;
  /**
   * Content to display in the page footer
   */
  footer?: React.ReactNode;
  /**
   * Main page content
   */
  children: Exclude<React.ReactNode, null | undefined>;
  /**
   * Additional CSS class name for the container
   */
  className?: string;
}

/**
 * PageLayout component provides a unified layout structure for all page-based components.
 *
 * Features:
 * - Responsive layouts using NDS breakpoints (480px, 768px, 1280px, 1440px)
 * - Automatic sidebar integration and padding adjustments
 * - Multiple layout types: single, dual-column, triple-column, custom-grid
 * - Progressive max-width constraints to prevent ultrawide expansion
 * - Optional header and footer sections
 */
const PageLayout: React.FC<PageLayoutProps> = ({
  layout = "single",
  columnRatio,
  maxWidth = "xl",
  header,
  footer,
  children,
  className,
}) => {
  const { l } = useBreakpoints();
  const { isCollapsed } = useSidebarContainer();
  const { isSidebarEnabled } = useIsSidebarEnabled();
  const isSidebarExpanded = isSidebarEnabled && !isCollapsed;

  // Force single column layout on mobile (below large breakpoint)
  const effectiveLayout = !l && layout !== "single" ? "single" : layout;

  const layoutClass = {
    single: styles["layout-single"],
    "dual-column": styles["layout-dual-column"],
    "triple-column": styles["layout-triple-column"],
    "custom-grid": styles["layout-custom-grid"],
  }[effectiveLayout];

  const containerClass = cc([
    styles["page-container"],
    {
      [styles["sidebar-expanded"]]: isSidebarExpanded,
    },
  ]);

  const wrapperClass = cc([
    styles["page-wrapper"],
    styles[`max-width-${maxWidth}`],
    layoutClass,
    {
      [styles["with-sidebar"]]: isSidebarEnabled,
    },
    className,
  ]);

  const style = {
    ...(columnRatio ? { "--column-ratio": columnRatio } : {}),
  } as React.CSSProperties;

  return (
    <div className={containerClass}>
      <div className={wrapperClass} style={style}>
        {header && <header className={styles["page-header"]}>{header}</header>}
        {children}
        {footer && <footer className={styles["page-footer"]}>{footer}</footer>}
      </div>
    </div>
  );
};

export default PageLayout;
